import type { CSSProperties, FC, ReactNode } from 'react'

import { useState } from 'react'
import { MessageCircleQuestion, Plus } from 'lucide-react'
import { cn } from '@/lib/utils'
import { ButtonLink } from '@/components/generals/button'
import { Scallop } from '@/components/generals/scallop'
import { SectionHeading } from '@/components/generals/section-heading'
import { FAQS } from '@/constants/home'

export const Faq: FC = (): ReactNode => {
    const [open, setOpen] = useState<number | null>(0)

    return (
        <section id='faq' aria-labelledby='faq-title' className='relative bg-cream-deep py-24 sm:py-32'>
            <Scallop className='text-cream-deep' />

            <div className='mx-auto grid max-w-6xl gap-12 px-5 sm:px-8 lg:grid-cols-[0.8fr_1.2fr] lg:gap-16'>
                <div className='lg:sticky lg:top-32 lg:self-start'>
                    <SectionHeading
                        id='faq-title'
                        lines={['Questions?', 'Answers.']}
                        sticker='FAQ'
                        stickerShape='burst'
                        stickerColor='text-lavender'
                        align='left'
                        stickerClassName='-left-4 sm:-left-10'
                    />

                    <div className='group reveal-pop mt-10 rounded-[2rem] border-2 border-plum bg-butter p-6 shadow-[0_5px_0_0_var(--color-plum)] [--reveal-delay:300ms]'>
                        <MessageCircleQuestion className='size-8 text-plum transition-transform duration-500 ease-[cubic-bezier(0.34,1.56,0.64,1)] group-hover:-rotate-12 group-hover:scale-125' aria-hidden='true' />

                        <p className='mt-3 font-heading text-2xl leading-tight text-plum'>
                            Still wondering about something?
                        </p>

                        <p className='mt-2 text-plum/80'>
                            Send us your question and a real human will reply within one business day.
                        </p>

                        <ButtonLink href='#contact' variant='secondary' size='sm' className='mt-5'>
                            Ask us anything
                        </ButtonLink>
                    </div>
                </div>

                <ul className='space-y-4'>
                    {FAQS.map((faq, index) => {
                        const isOpen = open === index
                        const triggerId = `faq-trigger-${index}`
                        const panelId = `faq-panel-${index}`

                        return (
                            <li
                                key={faq.question}
                                className='reveal'
                                style={{ '--reveal-delay': `${index * 70}ms` } as CSSProperties}
                            >
                                <div
                                    className={cn(
                                        'rounded-[1.75rem] border-2 border-plum transition-[background-color,box-shadow] duration-300',
                                        isOpen ? 'bg-cream shadow-[0_5px_0_0_var(--color-plum)]' : 'bg-cream/60 hover:bg-cream hover:shadow-[0_3px_0_0_var(--color-plum)]'
                                    )}
                                >
                                    <h3>
                                        <button
                                            id={triggerId}
                                            type='button'
                                            aria-expanded={isOpen}
                                            aria-controls={panelId}
                                            onClick={() => setOpen(isOpen ? null : index)}
                                            className='group flex w-full items-center justify-between gap-4 px-6 py-5 text-left'
                                        >
                                            <span className='font-heading text-[1.4rem] leading-tight text-plum sm:text-2xl'>
                                                {faq.question}
                                            </span>

                                            <span
                                                className={cn(
                                                    'grid size-10 shrink-0 place-items-center rounded-full border-2 border-plum text-plum transition-all duration-300 group-hover:scale-110 group-hover:bg-butter',
                                                    isOpen ? 'bg-butter' : 'bg-cream'
                                                )}
                                            >
                                                <Plus
                                                    aria-hidden='true'
                                                    className={cn('size-5 transition-transform duration-300', isOpen && 'rotate-45')}
                                                />
                                            </span>
                                        </button>
                                    </h3>

                                    <div
                                        id={panelId}
                                        role='region'
                                        aria-labelledby={triggerId}
                                        inert={!isOpen}
                                        className={cn(
                                            'grid transition-[grid-template-rows] duration-300 ease-out',
                                            isOpen ? 'grid-rows-[1fr]' : 'grid-rows-[0fr]'
                                        )}
                                    >
                                        <div className='overflow-hidden'>
                                            <p className='px-6 pb-6 leading-relaxed text-plum/80'>
                                                {faq.answer}
                                            </p>
                                        </div>
                                    </div>
                                </div>
                            </li>
                        )
                    })}
                </ul>
            </div>
        </section>
    )
}
